import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Alert,
  Chip,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchImages, fetchStats } from "@/store/imagesSlice";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { DASHBOARD_SORT, type DashboardSort } from "@/constants/dashboard";
import { routes } from "@/constants/routes";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, stats, loading } = useAppSelector((s) => s.images);
  const { hasKey, keyCount } = useLocalKeys();

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<DashboardSort>(DASHBOARD_SORT.newest);

  const loadData = useCallback(() => {
    dispatch(fetchImages({ search: search || undefined, sort }));
    dispatch(fetchStats());
  }, [dispatch, search, sort]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const usedPercent = stats ? Math.round((stats.used / stats.limit) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {keyCount === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("dashboard.noKeysWarning")}
        </Alert>
      )}
      {/* Storage progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            {t("dashboard.storage")}
          </Typography>
          {stats ? (
            <>
              <LinearProgress
                variant="determinate"
                value={usedPercent}
                sx={{ height: 10, borderRadius: 5, mb: 1 }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {stats.used} / {stats.limit} {t("dashboard.imagesUsed")} (
                {usedPercent}%)
              </Typography>
            </>
          ) : (
            <Skeleton variant="rectangular" height={10} />
          )}
        </CardContent>
      </Card>
      {/* Search + sort + upload */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder={t("dashboard.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("dashboard.sort")}</InputLabel>
          <Select
            value={sort}
            label={t("dashboard.sort")}
            onChange={(e) => setSort(e.target.value as DashboardSort)}
          >
            <MenuItem value={DASHBOARD_SORT.newest}>
              {t("dashboard.newest")}
            </MenuItem>
            <MenuItem value={DASHBOARD_SORT.oldest}>
              {t("dashboard.oldest")}
            </MenuItem>
            <MenuItem value={DASHBOARD_SORT.name}>
              {t("dashboard.name")}
            </MenuItem>
            <MenuItem value={DASHBOARD_SORT.size}>
              {t("dashboard.size")}
            </MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => navigate(routes.upload)}
        >
          {t("dashboard.upload")}
        </Button>
      </Box>
      {/* Image grid */}
      {loading ? (
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton
                variant="rectangular"
                height={160}
                sx={{ borderRadius: 1 }}
              />
            </Grid>
          ))}
        </Grid>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography
            sx={{
              color: "text.secondary",
            }}
          >
            {t("dashboard.noImages")}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {items.map((img) => {
            const locked = !hasKey(img.id);
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={img.id}>
                <Card>
                  <CardActionArea
                    onClick={() => navigate(routes.image(img.id))}
                    sx={{ p: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ maxWidth: "70%" }}
                      >
                        {img.name}
                      </Typography>
                      <Chip
                        icon={locked ? <LockIcon /> : <LockOpenIcon />}
                        label={
                          locked
                            ? t("dashboard.locked")
                            : t("dashboard.unlockable")
                        }
                        size="small"
                        color={locked ? "error" : "success"}
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {(img.size / 1024).toFixed(1)} KB
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                      }}
                    >
                      {new Date(img.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
